import { useState, useEffect } from "react";
import axios from "axios";
import { API, useTheme } from "../../App";
import { useAdminAuth } from "./AdminLayout";
import { PreviewStatsCard } from "./PreviewStatsCard";
import { FoundingPromoCard } from "./FoundingPromoCard";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { 
  LayoutDashboard, 
  Package, 
  BookOpen, 
  Users, 
  DollarSign,
  ShoppingBag,
  Eye,
  ExternalLink,
  Loader2,
  Crown,
  Lock,
  Unlock,
  AlertTriangle,
  Mail,
  Send,
  History,
  BarChart3,
  TrendingUp,
  Globe,
  Sparkles,
  Music,
  Gift,
  Download,
  FileText,
  Plus,
  X,
} from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { token } = useAdminAuth();
  const { theme, setTheme } = useTheme();
  const [themeLoading, setThemeLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [gatedMode, setGatedMode] = useState(true);
  const [gatedModeLoading, setGatedModeLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [deal, setDeal] = useState(null);
  const [dealLoading, setDealLoading] = useState(false);
  const [leads, setLeads] = useState(null); // null = not loaded, [] = empty
  const [leadsLoading, setLeadsLoading] = useState(false);

  // Targeted email send (one-off to a specific list of recipients)
  const [showTargetedForm, setShowTargetedForm] = useState(false);
  const [targetedRecipients, setTargetedRecipients] = useState("");
  const [targetedSubject, setTargetedSubject] = useState("");
  const [targetedMessage, setTargetedMessage] = useState("");
  // Optional PDF attachment for the broadcast (Resend supports up to 40MB total per email).
  const [targetedAttachments, setTargetedAttachments] = useState([]); // [{filename, content_b64, content_type, size}]
  const [targetedAudience, setTargetedAudience] = useState("manual");  // manual | waitlist | non_subscribers | etc.
  const [audienceCounts, setAudienceCounts] = useState(null);
  const [sendingTargeted, setSendingTargeted] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchGatedMode();
    fetchBroadcasts();
    fetchAnalytics();
    fetchDeal();
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    setLeadsLoading(true);
    try {
      const r = await axios.get(`${API}/admin/leads`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setLeads(r.data);
    } catch (e) {
      console.error("Failed to fetch leads", e);
    } finally {
      setLeadsLoading(false);
    }
  };

  const [resendingMissing, setResendingMissing] = useState(false);
  const resendMissingFreeSongEmails = async () => {
    const missing = (leads?.leads || []).filter(l => !l.email_sent).length;
    if (!missing) {
      toast.info("No pending leads — everyone's already received their email.");
      return;
    }
    const ok = window.confirm(
      `Re-send the free-song email to ${missing} ${missing === 1 ? "lead" : "leads"} who didn't get it the first time?`
    );
    if (!ok) return;
    setResendingMissing(true);
    try {
      const r = await axios.post(
        `${API}/admin/leads/resend-missing-emails`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (r.data.sent) {
        toast.success(`Sent ${r.data.sent} email${r.data.sent === 1 ? "" : "s"}. ${r.data.failed_count ? `(${r.data.failed_count} failed)` : ""}`);
      } else if (r.data.failed_count) {
        toast.error(`All ${r.data.failed_count} sends failed. Check Resend setup.`);
      } else {
        toast.info("Nothing to resend.");
      }
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    } finally {
      setResendingMissing(false);
    }
  };

  const downloadLeadsCsv = () => {
    if (!leads?.leads?.length) {
      toast.info("No leads to export yet.");
      return;
    }
    const headers = ["Email", "Source", "Captured At", "Email Sent", "Downloaded", "Download Count", "Converted"];
    const rows = leads.leads.map((l) => [
      l.email,
      l.source || "",
      l.captured_at || "",
      l.email_sent ? "yes" : "no",
      l.downloaded_at || "",
      l.download_count || 0,
      l.converted ? "yes" : "no",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `barsongz-leads-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGatedMode = async () => {
    try {
      const response = await axios.get(`${API}/site-config/gated-mode`);
      setGatedMode(response.data.gated_mode);
    } catch (error) {
      console.error("Failed to fetch gated mode", error);
    }
  };

  const toggleGatedMode = async () => {
    setGatedModeLoading(true);
    const newValue = !gatedMode;
    try {
      await axios.put(`${API}/admin/site-config`, 
        { gated_mode: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGatedMode(newValue);
      toast.success(newValue ? "Site is now GATED (launch mode)" : "Site is now OPEN to the public!");
    } catch (error) {
      console.error("Failed to update gated mode", error);
      toast.error("Failed to update site mode");
    } finally {
      setGatedModeLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const handlePreviewAsUser = async () => {
    setPreviewLoading(true);
    try {
      const response = await axios.post(`${API}/admin/preview-token`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const previewToken = response.data.preview_token;
      const baseUrl = window.location.origin;
      const previewUrl = `${baseUrl}/preview?token=${previewToken}`;
      
      const newTab = window.open(previewUrl, '_blank');
      
      if (newTab) {
        toast.success("Preview mode opened in new tab");
      } else {
        toast.error("Popup blocked! Copy this link and paste in a new tab:");
        toast.info(previewUrl, { duration: 15000 });
      }
    } catch (error) {
      console.error("Failed to start preview", error);
      toast.error("Failed to start preview mode");
    } finally {
      setPreviewLoading(false);
    }
  };

  const fetchBroadcasts = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-broadcasts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBroadcasts(response.data);
    } catch (error) {
      console.error("Failed to fetch broadcasts", error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to fetch analytics", error);
    }
  };

  const fetchDeal = async () => {
    try {
      const response = await axios.get(`${API}/deal`);
      setDeal(response.data);
    } catch (error) {
      console.error("Failed to fetch deal", error);
    }
  };

  const handleCreateDeal = async (price, days) => {
    setDealLoading(true);
    try {
      await axios.post(`${API}/admin/deal`, {
        deal_price: price,
        duration_days: days,
        label: "Launch Special"
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Deal created: $${price}/mo for ${days} days`);
      fetchDeal();
    } catch (error) {
      toast.error("Failed to create deal");
    } finally {
      setDealLoading(false);
    }
  };

  const handleCancelDeal = async () => {
    setDealLoading(true);
    try {
      await axios.delete(`${API}/admin/deal`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deal cancelled");
      setDeal(null);
      fetchDeal();
    } catch (error) {
      toast.error("Failed to cancel deal");
    } finally {
      setDealLoading(false);
    }
  };

  const handleThemeChange = async (next) => {
    if (next === theme) return;
    setThemeLoading(true);
    try {
      await axios.put(
        `${API}/admin/site-config`,
        { theme: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTheme(next);
      toast.success(
        next === "lavender"
          ? "Switched to Lavender theme (white & purple)"
          : "Switched to Classic theme (dark green & gold)"
      );
    } catch (error) {
      toast.error("Failed to switch theme");
    } finally {
      setThemeLoading(false);
    }
  };



  const [resendingId, setResendingId] = useState(null);
  const [testingSender, setTestingSender] = useState(false);
  const [dedupeLoading, setDedupeLoading] = useState(false);
  const [dedupeReport, setDedupeReport] = useState(null);

  const handleDedupePreview = async () => {
    setDedupeLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/content/dedupe-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDedupeReport(resp.data);
      if (resp.data.total_to_delete === 0) {
        toast.success("No duplicates found. Library is clean.");
      } else {
        toast.info(`Would remove ${resp.data.total_to_delete} duplicate row(s). Review below and click "Clean Duplicates" to confirm.`);
      }
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to preview cleanup");
    } finally {
      setDedupeLoading(false);
    }
  };

  const handleDedupeRun = async () => {
    if (!window.confirm("Remove duplicate songs (keeping the newest with audio per topic)?\n\nDeleted rows will be archived so you can recover them if needed.")) {
      return;
    }
    setDedupeLoading(true);
    try {
      const resp = await axios.post(
        `${API}/admin/content/dedupe`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDedupeReport(resp.data);
      toast.success(resp.data.message || `Cleaned ${resp.data.deleted_count} duplicate(s).`);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to clean duplicates");
    } finally {
      setDedupeLoading(false);
    }
  };

  const handleCleanDemo = async () => {
    if (!window.confirm("Remove all demo/placeholder audio (SoundHelix tracks, broken file_urls, etc.)?\n\nDeleted rows will be archived so you can recover them if needed.")) {
      return;
    }
    setDedupeLoading(true);
    try {
      const resp = await axios.post(
        `${API}/admin/content/clean-demo`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDedupeReport({
        deleted_count: resp.data.deleted_count,
        groups: (resp.data.examples || []).map((e) => ({
          course: "(demo)",
          topic: e.title || "?",
          kept_id: e.file_url,
        })),
      });
      toast.success(resp.data.message);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to remove demo content");
    } finally {
      setDedupeLoading(false);
    }
  };

  const handleTestSender = async () => {
    const defaultEmail = "nyemataylorwhite@gmail.com";
    const recipient = window.prompt(
      "Send a test email to which inbox?\n\n(Use your own email so you can verify the 'From' address with your own eyes.)",
      defaultEmail
    );
    if (!recipient || !recipient.trim()) return;
    setTestingSender(true);
    try {
      const response = await axios.post(
        `${API}/admin/email-test`,
        { to: recipient.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { expected_from, actual_from, sender_ok, warning, to } = response.data;
      if (sender_ok) {
        toast.success(`Test email sent from ${actual_from} to ${to}. Check your inbox to confirm!`, { duration: 12000 });
      } else if (warning) {
        toast.error(`Resend overrode sender to ${actual_from}. Expected ${expected_from}. ${warning}`, { duration: 15000 });
      } else if (actual_from) {
        toast.info(`Test email sent to ${to}. Actual sender: ${actual_from} (expected ${expected_from})`, { duration: 12000 });
      } else {
        toast.success(`Test email sent to ${to} (sender verification pending - check the inbox).`, { duration: 10000 });
      }
    } catch (error) {
      console.error("Failed to send test email", error);
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setTestingSender(false);
    }
  };

  const handleResendMissed = async (broadcastId, subject) => {
    if (!window.confirm(`Resend "${subject}" to any recipients who didn't receive it?\n\nThis will only send to those who missed it (failed deliveries + anyone not yet attempted).`)) {
      return;
    }
    setResendingId(broadcastId);
    try {
      const response = await axios.post(
        `${API}/admin/broadcast/${broadcastId}/resend-missed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "Resend complete");
      fetchBroadcasts();
    } catch (error) {
      console.error("Failed to resend missed", error);
      toast.error(error.response?.data?.detail || "Failed to resend to missed recipients");
    } finally {
      setResendingId(null);
    }
  };

  const handleSendUpdate = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error("Please fill in both subject and message");
      return;
    }
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/admin/send-update`, {
        subject: emailSubject,
        message: emailMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setEmailSubject("");
      setEmailMessage("");
      setShowEmailForm(false);
      fetchBroadcasts();
    } catch (error) {
      console.error("Failed to send update", error);
      toast.error(error.response?.data?.detail || "Failed to send update email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleAttachmentPick = async (fileList) => {
    if (!fileList || !fileList.length) return;
    const MAX_TOTAL = 10 * 1024 * 1024; // 10 MB safety cap — base64 inflates ~33%, and Gmail/Outlook reject inbound over ~25 MB. Keeps every send safe.
    const readAsBase64 = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const s = String(reader.result || "");
          const comma = s.indexOf(",");
          resolve(comma >= 0 ? s.slice(comma + 1) : s);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

    const existingSize = targetedAttachments.reduce((s, a) => s + (a.size || 0), 0);
    const additions = [];
    for (const file of Array.from(fileList)) {
      if (existingSize + additions.reduce((s, a) => s + a.size, 0) + file.size > MAX_TOTAL) {
        toast.error(`"${file.name}" would push total attachments over 10 MB — skipped`);
        continue;
      }
      try {
        const b64 = await readAsBase64(file);
        additions.push({
          filename: file.name,
          content_b64: b64,
          content_type: file.type || "application/octet-stream",
          size: file.size,
        });
      } catch {
        toast.error(`Could not read ${file.name}`);
      }
    }
    if (additions.length) {
      setTargetedAttachments((prev) => [...prev, ...additions]);
      toast.success(`Attached ${additions.length} file${additions.length === 1 ? "" : "s"}`);
    }
  };

  const removeAttachment = (idx) => {
    setTargetedAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSendTargetedEmail = async () => {
    if (!targetedSubject.trim() || !targetedMessage.trim()) {
      toast.error("Please fill in both subject and message");
      return;
    }
    let payload = { subject: targetedSubject, message: targetedMessage };
    if (targetedAudience === "manual") {
      const parsed = targetedRecipients
        .split(/[\s,;]+/)
        .map(e => e.trim().toLowerCase())
        .filter(e => e && e.includes("@"));
      if (!parsed.length) {
        toast.error("Please add at least one valid email address");
        return;
      }
      payload.recipients = parsed;
    } else {
      payload.audience = targetedAudience;
    }
    if (targetedAttachments.length) {
      // Strip the local-only `size` field before sending — server doesn't need it.
      payload.attachments = targetedAttachments.map((a) => ({
        filename: a.filename,
        content_b64: a.content_b64,
        content_type: a.content_type,
      }));
    }
    setSendingTargeted(true);
    try {
      const response = await axios.post(`${API}/admin/send-targeted-email`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sentCount = response.data?.sent_count ?? 0;
      const failedCount = response.data?.failed_count ?? 0;
      if (sentCount === 0 && failedCount > 0) {
        toast.error(
          `⚠️ Sent to 0 of ${failedCount} recipient${failedCount === 1 ? "" : "s"}. ` +
          `Resend rejected the batch — check your RESEND_API_KEY in Deploy → Secrets.`
        );
        // Keep the composer open so the user can retry without losing state.
      } else if (failedCount > 0) {
        toast.warning(
          `Sent to ${sentCount} of ${sentCount + failedCount} recipient${(sentCount + failedCount) === 1 ? "" : "s"}. ${failedCount} failed — check email_broadcast_recipients for details.`
        );
        setTargetedSubject("");
        setTargetedMessage("");
        setTargetedRecipients("");
        setTargetedAudience("manual");
        setTargetedAttachments([]);
        setShowTargetedForm(false);
        fetchBroadcasts();
      } else {
        toast.success(response.data.message || `Sent to ${sentCount} recipient${sentCount === 1 ? "" : "s"} ✅`);
        setTargetedSubject("");
        setTargetedMessage("");
        setTargetedRecipients("");
        setTargetedAudience("manual");
        setTargetedAttachments([]);
        setShowTargetedForm(false);
        fetchBroadcasts();
      }
    } catch (error) {
      console.error("Failed to send targeted email", error);
      toast.error(error.response?.data?.detail || "Failed to send targeted email");
    } finally {
      setSendingTargeted(false);
    }
  };

  // Load audience counts whenever the targeted form opens
  useEffect(() => {
    if (!showTargetedForm || !token) return;
    axios
      .get(`${API}/admin/audience-counts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAudienceCounts(r.data))
      .catch(() => setAudienceCounts(null));
  }, [showTargetedForm, token]);

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="font-serif text-2xl font-bold">Dashboard</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    { label: "Total Revenue", value: `$${stats?.total_revenue?.toFixed(2) || '0.00'}`, icon: DollarSign, color: "text-green-500" },
    { label: "Total Orders", value: stats?.total_purchases || 0, icon: ShoppingBag, color: "text-blue-500" },
    { label: "Total Users", value: stats?.total_users || 0, icon: Users, color: "text-purple-500" },
    { label: "Total Topics", value: stats?.total_topics || 0, icon: BookOpen, color: "text-gold" },
    { label: "Total Bundles", value: stats?.total_bundles || 0, icon: Package, color: "text-orange-500" },
  ];

  const foundingMembers = stats?.founding_members;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LayoutDashboard className="h-8 w-8 text-gold" />
          <h1 className="font-serif text-2xl font-bold">Dashboard</h1>
        </div>
        <Button 
          onClick={handlePreviewAsUser}
          disabled={previewLoading}
          className="bg-gold text-primary-foreground hover:bg-gold/90"
        >
          {previewLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <Eye className="h-4 w-4 mr-2" />
              Preview as User
              <ExternalLink className="h-3 w-3 ml-2" />
            </>
          )}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-2xl font-bold mt-1">{stat.value}</p>
                </div>
                <stat.icon className={`h-8 w-8 ${stat.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <FoundingPromoCard />

      <PreviewStatsCard />

      {/* Site Analytics */}
      {analytics && (
        <Card className="bg-card border-border" data-testid="analytics-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <BarChart3 className="h-5 w-5 text-gold" />
              <CardTitle className="font-serif">Site Traffic</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {/* Traffic summary row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: "Today", value: analytics.today_views, icon: Eye },
                { label: "This Week", value: analytics.week_views, icon: TrendingUp },
                { label: "This Month", value: analytics.month_views, icon: BarChart3 },
                { label: "All Time", value: analytics.total_views, icon: Globe },
              ].map((item, i) => (
                <div key={i} className="p-3 bg-muted/30 rounded-lg text-center" data-testid={`analytics-${item.label.toLowerCase().replace(" ", "-")}`}>
                  <item.icon className="h-4 w-4 text-gold mx-auto mb-1" />
                  <p className="text-2xl font-bold">{item.value}</p>
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                </div>
              ))}
            </div>

            {/* Daily views bar chart */}
            {analytics.daily_views?.length > 0 && (
              <div className="mb-6">
                <p className="text-sm font-medium mb-3">Last 7 Days</p>
                <div className="flex items-end gap-1 h-24">
                  {analytics.daily_views.map((day, i) => {
                    const maxViews = Math.max(...analytics.daily_views.map(d => d.views), 1);
                    const heightPct = (day.views / maxViews) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1" data-testid={`analytics-bar-${i}`}>
                        <span className="text-[10px] text-muted-foreground">{day.views}</span>
                        <div
                          className="w-full bg-gold/70 rounded-t min-h-[2px]"
                          style={{ height: `${Math.max(heightPct, 3)}%` }}
                        />
                        <span className="text-[10px] text-muted-foreground">
                          {day.date.slice(5)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Top pages */}
            {analytics.top_pages?.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-3">Top Pages</p>
                <div className="space-y-2">
                  {analytics.top_pages.slice(0, 5).map((page, i) => (
                    <div key={i} className="flex items-center justify-between text-sm" data-testid={`analytics-page-${i}`}>
                      <span className="text-muted-foreground font-mono text-xs">{page.page}</span>
                      <span className="font-medium">{page.views} views</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Site Mode Control */}
      <Card className={`border-2 ${gatedMode ? 'border-orange-500/50 bg-orange-500/5' : 'border-green-500/50 bg-green-500/5'}`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${gatedMode ? 'bg-orange-500/20' : 'bg-green-500/20'}`}>
                {gatedMode ? (
                  <Lock className="h-6 w-6 text-orange-500" />
                ) : (
                  <Unlock className="h-6 w-6 text-green-500" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  Site Mode
                  {gatedMode && <span className="text-xs bg-orange-500/20 text-orange-500 px-2 py-0.5 rounded">PRE-LAUNCH</span>}
                  {!gatedMode && <span className="text-xs bg-green-500/20 text-green-500 px-2 py-0.5 rounded">LIVE</span>}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {gatedMode 
                    ? "Site shows only the landing page with email capture" 
                    : "Full site is accessible to all visitors"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right mr-4">
                <p className="text-sm font-medium">{gatedMode ? 'Gated' : 'Open'}</p>
                <p className="text-xs text-muted-foreground">Click to toggle</p>
              </div>
              <Switch
                checked={!gatedMode}
                onCheckedChange={toggleGatedMode}
                disabled={gatedModeLoading}
                className="data-[state=checked]:bg-green-500"
              />
            </div>
          </div>
          {!gatedMode && (
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-500">
                Your site is now public! All visitors can browse the library and create accounts.
              </p>
            </div>
          )}
        </CardContent>
      </Card>


      {/* Duplicate Songs Cleanup */}
      <Card className="bg-card border-border" data-testid="dedupe-card">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4 flex-1">
              <div className="p-3 bg-gold/10 rounded-full">
                <Music className="h-6 w-6 text-gold" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Library Cleanup</h3>
                <p className="text-muted-foreground text-sm">
                  Remove duplicate songs so only one entry exists per (course + topic).
                  Deleted rows are archived for 30 days in case you need them back.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCleanDemo}
                disabled={dedupeLoading}
                data-testid="clean-demo-btn"
                title="Remove SoundHelix demos and other junk audio rows"
              >
                Remove Demo Tracks
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDedupePreview}
                disabled={dedupeLoading}
                data-testid="dedupe-preview-btn"
              >
                Preview Dupes
              </Button>
              <Button
                onClick={handleDedupeRun}
                size="sm"
                disabled={dedupeLoading}
                data-testid="dedupe-run-btn"
                className="bg-gold text-primary-foreground hover:bg-gold/90"
              >
                {dedupeLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Clean Duplicates
              </Button>
            </div>
          </div>
          {dedupeReport && (
            <div className="mt-4 p-3 rounded-lg bg-muted/40 border border-border text-sm">
              <p className="font-medium mb-1">
                {dedupeReport.total_to_delete !== undefined
                  ? `Would remove ${dedupeReport.total_to_delete} duplicate row(s):`
                  : `Cleaned ${dedupeReport.deleted_count || 0} duplicate row(s).`}
              </p>
              <ul className="space-y-0.5 text-xs text-muted-foreground max-h-40 overflow-y-auto">
                {(dedupeReport.groups || []).map((g, i) => (
                  <li key={i}>
                    {g.course} / {g.topic}: keep <span className="font-mono">{g.would_keep || g.kept_id}</span>
                    {" "}— remove {g.would_delete?.length ?? g.removed} other(s)
                  </li>
                ))}
                {(dedupeReport.groups || []).length === 0 && (
                  <li>Library is already clean - no duplicates.</li>
                )}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>


      {/* Theme toggle card removed Feb 2026 — lavender is the only theme. */}

      {/* Deal Management */}
      <Card className="bg-card border-border" data-testid="deal-management-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <DollarSign className="h-5 w-5 text-gold" />
            <CardTitle className="font-serif">Limited-Time Deal</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {deal?.active ? (
            <div className="space-y-4">
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-amber-400">{deal.label}</span>
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/40">Active</Badge>
                </div>
                <p className="text-2xl font-bold">${deal.deal_price}<span className="text-sm text-muted-foreground font-normal">/mo</span></p>
                <p className="text-sm text-muted-foreground mt-1">
                  Original founding price: ${deal.original_price}/mo
                </p>
                <p className="text-sm text-muted-foreground">
                  Expires: {new Date(deal.end_date).toLocaleDateString()}
                </p>
              </div>
              <Button
                variant="destructive"
                className="w-full"
                onClick={handleCancelDeal}
                disabled={dealLoading}
                data-testid="cancel-deal-btn"
              >
                {dealLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Cancel Deal
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                No active deal. Create a limited-time founding member discount.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  className="bg-gold text-primary-foreground hover:bg-gold/90"
                  onClick={() => handleCreateDeal(10, 30)}
                  disabled={dealLoading}
                  data-testid="create-deal-10-btn"
                >
                  {dealLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  $10/mo for 30 days
                </Button>
                <Button
                  variant="outline"
                  className="border-gold/50 text-gold hover:bg-gold hover:text-primary-foreground"
                  onClick={() => handleCreateDeal(15, 14)}
                  disabled={dealLoading}
                  data-testid="create-deal-15-btn"
                >
                  $15/mo for 14 days
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Founding Member Stats */}
      {foundingMembers && (
        <Card className="bg-gradient-to-r from-gold/10 to-gold/5 border-gold/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gold/20 rounded-full">
                  <Crown className="h-6 w-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Founding Members</h3>
                  <p className="text-muted-foreground text-sm">
                    Code: <span className="font-mono bg-muted px-2 py-0.5 rounded">FOUNDER</span>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-gold">{foundingMembers.count} / {foundingMembers.limit}</p>
                <p className="text-sm text-muted-foreground">
                  {foundingMembers.remaining > 0 
                    ? `${foundingMembers.remaining} spots remaining` 
                    : 'All spots claimed!'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Broadcast */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-gold" />
              <CardTitle className="font-serif">Email Updates</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="test-sender-btn"
                onClick={handleTestSender}
                disabled={testingSender}
                variant="outline"
                size="sm"
                title="Send a test email to yourself and verify the sender address Resend actually used"
              >
                {testingSender ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Test Sender
              </Button>
              <Button
                data-testid="send-update-toggle-btn"
                onClick={() => setShowEmailForm(!showEmailForm)}
                variant={showEmailForm ? "outline" : "default"}
                className={showEmailForm ? "" : "bg-gold text-primary-foreground hover:bg-gold/90"}
                size="sm"
              >
                {showEmailForm ? "Cancel" : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Update
                  </>
                )}
              </Button>
              <Button
                data-testid="send-targeted-toggle-btn"
                onClick={() => setShowTargetedForm(!showTargetedForm)}
                variant={showTargetedForm ? "outline" : "default"}
                className={showTargetedForm ? "" : "bg-primary text-primary-foreground hover:bg-primary/90"}
                size="sm"
                title="Send a one-off email to a specific list of recipients (e.g. payment-glitch recovery)"
              >
                {showTargetedForm ? "Cancel" : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Targeted Send
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {showEmailForm && (
            <div className="space-y-4 mb-6 p-4 bg-muted/50 rounded-lg border border-border">
              <Button
                variant="outline"
                size="sm"
                className="border-gold/50 text-gold hover:bg-gold hover:text-primary-foreground"
                onClick={() => {
                  setEmailSubject("Bar Songz just got a glow-up — come see.");
                  setEmailMessage(`Hey friend,\n\nA lot has changed at Bar Songz since you last visited \u2014 and I wanted you to be the first to see it.\n\nHere\u2019s what\u2019s new:\n\nA fresh look. We\u2019re now in lavender and white \u2014 a softer, yet cooler feel that actually fits what Bar Songz is about: bringing a fresh approach to traditional law school study by turning dense, black letter law concepts into songs you can hum \u2014 and retain.\n\nSimple pricing. The monthly subscription is $9.99/month. Cancel anytime, no hidden fees.\n\nCourse Packs are here. Don\u2019t want to commit to a subscription? Buy a 6-song Course Pack for $30, or build your own 4-song mix for $25. You download the MP3s and they\u2019re yours to play forever, on any device.\n\nA free song, just because. Drop your email anywhere on the site and we\u2019ll send you one full song \u2014 \u201CSide-Piece\u201D \u2014 yours to keep. No subscription required. No catch.\n\nOne more thing. I want to be transparent: I\u2019m Nye \u2014 a singer, songwriter, and non-traditional law student building Bar Songz one course at a time. In a world full of AI-generated faces and faceless brands, I think it\u2019s important that you know I\u2019m a real, breathing human being.\n\nIf you\u2019re skeptical (and you should be), come find me on TikTok: @itsfreedomfancie. You\u2019ll catch me singing gospel, talking about law school, and occasionally sharing pieces of the Bar Songz journey as it unfolds.\n\nThank you for being one of the early ones. The library is growing, the songs are getting better, and the best is still ahead.\n\n\u2014 Nye`);
                  toast.success("Relaunch email draft loaded");
                }}
                data-testid="load-draft-btn"
              >
                Load Relaunch Email Draft
              </Button>
              <div>
                <label className="text-sm font-medium mb-1 block">Subject</label>
                <input
                  data-testid="email-subject-input"
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="e.g. New Contracts Songs Available!"
                  className="w-full px-3 py-2 rounded-md bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-gold/50"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Message</label>
                <textarea
                  data-testid="email-message-input"
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Tell your subscribers what's new..."
                  rows={4}
                  className="w-full px-3 py-2 rounded-md bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 resize-none"
                />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  This will be sent to all subscribers and waitlist members.
                </p>
                <div className="flex gap-2">
                  <Button
                    data-testid="preview-email-btn"
                    variant="outline"
                    onClick={async () => {
                      if (!emailSubject.trim() || !emailMessage.trim()) return;
                      const win = window.open("", "_blank");
                      try {
                        const res = await axios.post(`${API}/admin/email-preview`, {
                          subject: emailSubject,
                          message: emailMessage
                        }, { headers: { Authorization: `Bearer ${token}` } });
                        win.document.write(res.data.html);
                        win.document.close();
                      } catch (e) {
                        if (win) win.close();
                        toast.error("Failed to preview email");
                      }
                    }}
                    disabled={!emailSubject.trim() || !emailMessage.trim()}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview
                  </Button>
                  <Button
                    data-testid="send-update-confirm-btn"
                    onClick={handleSendUpdate}
                    disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
                    className="bg-gold text-primary-foreground hover:bg-gold/90"
                  >
                    {sendingEmail ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send to All
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {showTargetedForm && (
            <div
              className="space-y-4 mb-6 p-4 bg-primary/5 rounded-lg border border-primary/30"
              data-testid="targeted-email-form"
            >
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-primary">Targeted send</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground"
                  onClick={() => {
                    setTargetedRecipients(
                      [
                        "kevinhill686@gmail.com",
                        "anbanks1@cougars.ccis.edu",
                        "1.bournej@gmail.com",
                        "jada.bourne@law.gwu.edu",
                        "ruckussjr12@gmail.com",
                        "cameront789@gmail.com",
                        "cameront99@gmail.com",
                      ].join("\n")
                    );
                    setTargetedSubject(
                      "Hey — we owe you a real apology (and your first month on us)"
                    );
                    setTargetedMessage(
`Hi there,

I noticed you signed up for Bar Songz recently but never made it past the payment page.

That wasn't you. That was us. A payment issue on our end was silently failing checkouts for a stretch of days, and instead of showing you a proper error, our site just… dropped you. I'm mortified, and I'm sorry.

The good news: it's fixed now. And to make it right, your first month is 100% on me — no charge, no catch:

Use code WEREBACK at checkout and your first month is free. After that it's $9.99/month — or you can cancel before month two with one click, no follow-up, no hassle.

[Claim your free month → https://barsongz.com/subscribe?code=WEREBACK]

If you were counting on Bar Songz for a specific bar exam, please give it another try. If something feels off during checkout this time, hit reply on this email and I'll help you personally.

Thanks for giving us a shot in the first place.

— Nye
Founder, Bar Songz`
                    );
                    toast.success("Payment-glitch recovery template loaded · 7 affected users pre-filled");
                  }}
                  data-testid="load-payment-glitch-template-btn"
                >
                  Load "Payment Glitch Recovery" template
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  className="border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground"
                  onClick={() => {
                    setTargetedAudience("non_subscribers");
                    setTargetedSubject(
                      "Half off your first month of Bar Songz."
                    );
                    setTargetedMessage(
`Hi friend,

If you've been on the fence, here's a nudge: your first month of Bar Songz is 50% off. About $5.

Yes, five bucks for a month of unlimited streaming, playlists, and our growing library of black-letter law set to music.

Stay, and it's $9.99/mo after that. If it's not a fit, you can cancel before month two with one click — no follow-up, no hassle.

Use code HALFOFF at checkout — or just tap the link below and it's applied for you.

— Nye
[Start for $5 → https://barsongz.com/subscribe?code=HALFOFF]`
                    );
                    toast.success("Half-off template loaded · audience set to non-subscribers");
                  }}
                  data-testid="load-halfoff-template-btn"
                >
                  Load "Half Off First Month" template
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  className="border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground"
                  onClick={() => {
                    setTargetedAudience("founders");
                    setTargetedSubject("Your founding rate is locked in. Thank you.");
                    setTargetedMessage(
`Hi friend,

Founding Member enrollment for Bar Songz is officially closed, and I wanted you to hear it from me first.

You were here early, when Bar Songz was just a handful of songs and a big idea. That meant everything. So here's my promise: your $5.99/month founding rate is locked in for life. It won't go up when new courses drop or when the library grows. And if you ever take a break and come back, your founding rate will be waiting for you.

New members now join at $9.99/month. You never will.

Thank you for believing in this before anyone else did. The best is still ahead.

— Nye
Founder, Bar Songz ⚖️🎵`
                    );
                    toast.success("Founder thank-you template loaded · audience set to Founding Members");
                  }}
                  data-testid="load-founder-thankyou-template-btn"
                >
                  Load "Founder Thank-You" template
                </Button>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  Send to
                </label>
                <select
                  data-testid="targeted-audience-select"
                  value={targetedAudience}
                  onChange={(e) => setTargetedAudience(e.target.value)}
                  className="w-full px-3 py-2 rounded-md bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="manual">Manual list (paste emails below)</option>
                  <option value="waitlist">
                    Waitlist signups{audienceCounts ? ` (${audienceCounts.waitlist})` : ""}
                  </option>
                  <option value="non_subscribers">
                    Everyone who hasn&apos;t subscribed yet{audienceCounts ? ` (${audienceCounts.non_subscribers})` : ""}
                  </option>
                  <option value="registered_no_sub">
                    Registered users without a subscription{audienceCounts ? ` (${audienceCounts.registered_no_sub})` : ""}
                  </option>
                  <option value="subscribers">
                    Active paid subscribers only{audienceCounts ? ` (${audienceCounts.subscribers})` : ""}
                  </option>
                  <option value="founders">
                    Founding Members{audienceCounts ? ` (${audienceCounts.founders})` : ""}
                  </option>
                  <option value="all">
                    Everyone (registered + waitlist){audienceCounts ? ` (${audienceCounts.all})` : ""}
                  </option>
                </select>
                {audienceCounts?.unsubscribed_excluded > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {audienceCounts.unsubscribed_excluded} unsubscribed contact{audienceCounts.unsubscribed_excluded === 1 ? "" : "s"} automatically excluded.
                  </p>
                )}
              </div>

              {targetedAudience === "manual" && (
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Recipients
                    <span className="text-xs text-muted-foreground ml-2">
                      (one per line, or comma-separated)
                    </span>
                  </label>
                  <textarea
                    data-testid="targeted-recipients-input"
                    value={targetedRecipients}
                    onChange={(e) => setTargetedRecipients(e.target.value)}
                    placeholder={"alice@example.com\nbob@example.com"}
                    rows={4}
                    className="w-full px-3 py-2 rounded-md bg-background border border-border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {(() => {
                      const n = targetedRecipients
                        .split(/[\s,;]+/)
                        .map(e => e.trim().toLowerCase())
                        .filter(e => e && e.includes("@")).length;
                      return n
                        ? `${n} valid email${n === 1 ? "" : "s"} detected`
                        : "No valid emails detected yet";
                    })()}
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium mb-1 block">Subject</label>
                <input
                  data-testid="targeted-subject-input"
                  type="text"
                  value={targetedSubject}
                  onChange={(e) => setTargetedSubject(e.target.value)}
                  placeholder="e.g. A quick note from Nye"
                  className="w-full px-3 py-2 rounded-md bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Message</label>
                <textarea
                  data-testid="targeted-message-input"
                  value={targetedMessage}
                  onChange={(e) => setTargetedMessage(e.target.value)}
                  placeholder="What do you want to say to these specific people?"
                  rows={10}
                  className="w-full px-3 py-2 rounded-md bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y"
                />
              </div>

              {/* PDF / file attachments */}
              <div className="rounded-md border border-dashed border-purple-300 bg-purple-50/40 p-3 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="h-4 w-4 text-purple-600" />
                    <span className="font-medium">Attach files (optional)</span>
                    <span className="text-xs text-muted-foreground">PDFs, max 10 MB total</span>
                  </div>
                  <label
                    className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 rounded border border-purple-300 bg-white text-sm text-purple-700 hover:bg-purple-100"
                    data-testid="targeted-attach-btn"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Choose files
                    <input
                      type="file"
                      accept="application/pdf"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        handleAttachmentPick(e.target.files);
                        e.target.value = "";
                      }}
                      data-testid="targeted-attach-input"
                    />
                  </label>
                </div>
                {targetedAttachments.length > 0 && (
                  <ul className="space-y-1" data-testid="targeted-attachment-list">
                    {targetedAttachments.map((a, idx) => (
                      <li
                        key={`${a.filename}-${idx}`}
                        className="flex items-center justify-between text-xs bg-white border border-purple-200 rounded px-2 py-1"
                      >
                        <span className="truncate">
                          <FileText className="inline h-3 w-3 mr-1 text-purple-600" />
                          {a.filename}
                          <span className="ml-2 text-muted-foreground">
                            ({(a.size / 1024).toFixed(1)} KB)
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(idx)}
                          className="ml-2 text-red-600 hover:text-red-800"
                          title="Remove attachment"
                          data-testid={`targeted-attachment-remove-${idx}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Only the addresses above will receive this email.
                  Unsubscribed addresses are skipped automatically.
                </p>
                <div className="flex gap-2">
                  <Button
                    data-testid="targeted-preview-btn"
                    variant="outline"
                    onClick={async () => {
                      if (!targetedSubject.trim() || !targetedMessage.trim()) return;
                      const win = window.open("", "_blank");
                      try {
                        const res = await axios.post(`${API}/admin/email-preview`, {
                          subject: targetedSubject,
                          message: targetedMessage,
                          audience: targetedAudience === "manual" ? null : targetedAudience,
                        }, { headers: { Authorization: `Bearer ${token}` } });
                        win.document.write(res.data.html);
                        win.document.close();
                      } catch (e) {
                        if (win) win.close();
                        toast.error("Failed to preview email");
                      }
                    }}
                    disabled={!targetedSubject.trim() || !targetedMessage.trim()}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview
                  </Button>
                  <Button
                    data-testid="targeted-send-confirm-btn"
                    onClick={handleSendTargetedEmail}
                    disabled={
                      sendingTargeted
                      || !targetedSubject.trim()
                      || !targetedMessage.trim()
                      || (targetedAudience === "manual" && !targetedRecipients.trim())
                    }
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    {sendingTargeted ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send to list
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Broadcast History */}
          {broadcasts.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <History className="h-4 w-4" />
                <span>Recent Broadcasts</span>
              </div>
              {broadcasts.slice(0, 5).map((bc, idx) => (
                <div key={idx} className="flex items-center justify-between gap-3 p-3 bg-muted rounded-lg">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate" data-testid={`broadcast-subject-${idx}`}>{bc.subject}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(bc.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-medium text-gold">{bc.sent_count} sent</p>
                    {bc.failed_count > 0 && (
                      <p className="text-xs text-red-400">{bc.failed_count} failed</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleResendMissed(bc.broadcast_id, bc.subject)}
                    disabled={resendingId === bc.broadcast_id}
                    data-testid={`resend-missed-btn-${idx}`}
                    title="Resend to anyone who didn't get this email"
                  >
                    {resendingId === bc.broadcast_id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Send className="h-3 w-3" />
                    )}
                    <span className="ml-1 hidden sm:inline">Resend Missed</span>
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4 text-sm">No broadcasts sent yet</p>
          )}
        </CardContent>
      </Card>

      {/* Free Song Leads */}
      <Card className="bg-card border-border" data-testid="admin-leads-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Gift className="h-5 w-5 text-gold" />
              <CardTitle className="font-serif">Free-Song Leads</CardTitle>
              {leads && (
                <Badge variant="outline" className="border-gold/50 text-gold">
                  {leads.total} captured
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={fetchLeads}
                disabled={leadsLoading}
                data-testid="refresh-leads-btn"
              >
                {leadsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={resendMissingFreeSongEmails}
                disabled={resendingMissing || !leads?.leads?.some(l => !l.email_sent)}
                className="border-purple-400 text-purple-700 hover:bg-purple-50"
                data-testid="resend-missing-leads-btn"
              >
                {resendingMissing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                Re-send to missing
              </Button>
              <Button
                size="sm"
                className="bg-gold text-primary-foreground hover:bg-gold/90"
                onClick={downloadLeadsCsv}
                disabled={!leads?.leads?.length}
                data-testid="export-leads-csv-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {leads && leads.total > 0 ? (
            <>
              {/* Stats strip */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="p-3 rounded-lg bg-muted text-center">
                  <p className="text-2xl font-bold text-gold">{leads.total}</p>
                  <p className="text-xs text-muted-foreground">Total Leads</p>
                </div>
                <div className="p-3 rounded-lg bg-muted text-center">
                  <p className="text-2xl font-bold text-gold">{leads.downloaded}</p>
                  <p className="text-xs text-muted-foreground">
                    Clicked Download · {leads.total > 0 ? Math.round((leads.downloaded / leads.total) * 100) : 0}%
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-muted text-center">
                  <p className="text-2xl font-bold text-gold">{leads.converted}</p>
                  <p className="text-xs text-muted-foreground">
                    Converted to Paid · {leads.total > 0 ? Math.round((leads.converted / leads.total) * 100) : 0}%
                  </p>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="leads-table">
                  <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left py-2 pr-3">Email</th>
                      <th className="text-left py-2 pr-3">Captured</th>
                      <th className="text-left py-2 pr-3">Source</th>
                      <th className="text-center py-2 pr-3">Email Sent</th>
                      <th className="text-center py-2 pr-3">Downloaded</th>
                      <th className="text-center py-2 pr-3">Converted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.leads.slice(0, 50).map((lead, i) => (
                      <tr key={lead.email + i} className="border-b border-border/50 hover:bg-muted/50">
                        <td className="py-2 pr-3 font-medium truncate max-w-[220px]" data-testid={`lead-email-${i}`}>
                          {lead.email}
                        </td>
                        <td className="py-2 pr-3 text-muted-foreground text-xs">
                          {lead.captured_at
                            ? new Date(lead.captured_at).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "—"}
                        </td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground">{lead.source || "—"}</td>
                        <td className="py-2 pr-3 text-center">
                          {lead.email_sent ? (
                            <span className="text-green-500">✓</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-center">
                          {lead.download_count > 0 ? (
                            <span className="text-gold font-medium">{lead.download_count}×</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-center">
                          {lead.converted ? (
                            <Badge className="bg-green-900/40 text-green-300 border-green-700/40">Paid</Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {leads.leads.length > 50 && (
                  <p className="text-xs text-muted-foreground text-center mt-3">
                    Showing first 50 of {leads.total}. Use Export CSV for the full list.
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <Gift className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
              <p className="text-muted-foreground text-sm">
                No leads yet. The free-song form goes live on the landing page.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Orders */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-serif">Recent Orders</CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.recent_purchases?.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_purchases.map((purchase, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">{purchase.user_email}</p>
                    <p className="text-sm text-muted-foreground">
                      {purchase.items?.length || 0} item(s) • {formatDate(purchase.created_at)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gold">${purchase.total?.toFixed(2)}</p>
                    <span className="text-xs px-2 py-1 rounded bg-green-900/50 text-green-300">
                      {purchase.payment_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No orders yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
